import api from '../../../api/client';

class ChatbotApi {
  constructor() {
    this.baseUrl = '/chatbot';
  }

  // Session management
  async createSession(data) {
    try {
      const response = await api.post(`${this.baseUrl}/sessions/`, data);
      return response.data;
    } catch (error) {
      console.error('Error creating session:', error);
      throw new Error(error.response?.data?.error || 'Failed to create session');
    }
  }

  async getSession(sessionId) {
    try {
      const response = await api.get(`${this.baseUrl}/sessions/${sessionId}/`);
      return response.data;
    } catch (error) {
      console.error('Error getting session:', error);
      throw new Error(error.response?.data?.error || 'Failed to get session');
    }
  }

  async endSession(sessionId) {
    try {
      const response = await api.delete(`${this.baseUrl}/sessions/${sessionId}/end/`);
      return response.data;
    } catch (error) {
      console.error('Error ending session:', error);
      throw new Error(error.response?.data?.error || 'Failed to end session');
    }
  }

  // Message handling
  async sendMessage(sessionId, data) {
    try {
      const response = await api.post(`${this.baseUrl}/sessions/${sessionId}/messages/`, data);
      return response.data;
    } catch (error) {
      console.error('Error sending message:', error);
      throw new Error(error.response?.data?.error || 'Failed to send message');
    }
  }

  async sendMessageStream(sessionId, data, onToken, onMetadata, onError, onDone) {
    try {
      const baseURL = api.defaults.baseURL || '/api/';
      const url = `${baseURL.replace(/\/$/, '')}${this.baseUrl}/sessions/${sessionId}/messages/stream/`;
      
      const headers = {
        'Content-Type': 'application/json',
        'Accept-Language': localStorage.getItem('i18nextLng') || 'en'
      };

      const csrfToken = document.cookie.split('; ')
        .find(row => row.startsWith('csrftoken='))
        ?.split('=')[1];
      if (csrfToken) {
        headers['X-CSRFToken'] = csrfToken;
      }

      const token = localStorage.getItem('token');
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      } else if (api.defaults.headers.common['Authorization']) {
        headers['Authorization'] = api.defaults.headers.common['Authorization'];
      }

      const response = await fetch(url, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(data),
        credentials: 'include'
      });

      if (!response.ok) {
        let errMessage = `HTTP error! status: ${response.status}`;
        try {
          const errText = await response.text();
          try {
            const errJson = JSON.parse(errText);
            if (errJson.error) errMessage = errJson.error;
            else if (errText) errMessage = errText;
          } catch {
            if (errText) errMessage = errText;
          }
        } catch (e) {
          console.error('Failed to read error response text:', e);
        }
        throw new Error(errMessage);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let isDone = false;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split(/\r?\n\r?\n/);
        buffer = parts.pop() || '';

        for (const part of parts) {
          if (!part.trim()) continue;
          const lines = part.split(/\r?\n/);
          let eventType = null;
          let dataStr = '';

          for (const line of lines) {
            if (line.startsWith('event: ')) {
              eventType = line.substring(7).trim();
            } else if (line.startsWith('data: ')) {
              dataStr += line.substring(6).trim();
            }
          }

          if (dataStr) {
            try {
              const parsedData = JSON.parse(dataStr);
              if (eventType === 'metadata' || parsedData.type === 'metadata') {
                if (onMetadata) onMetadata(parsedData);
              } else if (eventType === 'token' || parsedData.content !== undefined) {
                if (onToken) onToken(parsedData.content);
              } else if (eventType === 'error' || parsedData.error) {
                if (onError) onError(parsedData.error);
              } else if (eventType === 'done' || parsedData.status === 'completed') {
                isDone = true;
                if (onDone) onDone(parsedData);
              }
            } catch (e) {
              console.error('Error parsing SSE chunk:', e, dataStr);
            }
          }
        }
      }

      if (!isDone && onDone) {
        onDone({ status: 'completed' });
      }
    } catch (error) {
      console.error('Error in sendMessageStream:', error);
      if (onError) onError(error.message || 'Streaming failed');
      throw error;
    }
  }

  async getSessionMessages(sessionId) {
    try {
      const response = await api.get(`${this.baseUrl}/sessions/${sessionId}/messages/list/`);
      return response.data;
    } catch (error) {
      console.error('Error getting session messages:', error);
      throw new Error(error.response?.data?.error || 'Failed to get session messages');
    }
  }

  // Action handling
  async executeAction(sessionId, data) {
    try {
      const response = await api.post(`${this.baseUrl}/sessions/${sessionId}/actions/`, data);
      return response.data;
    } catch (error) {
      console.error('Error executing action:', error);
      throw new Error(error.response?.data?.error || 'Failed to execute action');
    }
  }

  async getSessionActions(sessionId) {
    try {
      const response = await api.get(`${this.baseUrl}/sessions/${sessionId}/actions/list/`);
      return response.data;
    } catch (error) {
      console.error('Error getting session actions:', error);
      throw new Error(error.response?.data?.error || 'Failed to get session actions');
    }
  }

  async getAvailableActions(sessionId) {
    try {
      const response = await api.get(`${this.baseUrl}/sessions/${sessionId}/actions/available/`);
      return response.data;
    } catch (error) {
      console.error('Error getting available actions:', error);
      throw new Error(error.response?.data?.error || 'Failed to get available actions');
    }
  }

  // Specialized endpoints
  async uploadResume(sessionId, file, description = '') {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('description', description);

      const response = await api.post(`${this.baseUrl}/sessions/${sessionId}/resume/upload/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error uploading resume:', error);
      throw new Error(error.response?.data?.error || 'Failed to upload resume');
    }
  }

  async findJobMatches(sessionId, data = {}) {
    try {
      const response = await api.post(`${this.baseUrl}/sessions/${sessionId}/jobs/matches/`, data);
      return response.data;
    } catch (error) {
      console.error('Error finding job matches:', error);
      throw new Error(error.response?.data?.error || 'Failed to find job matches');
    }
  }

  // Utility methods
  async updateSessionContext(sessionId, contextData) {
    try {
      const response = await api.patch(`${this.baseUrl}/sessions/${sessionId}/`, {
        context_data: contextData
      });
      return response.data;
    } catch (error) {
      console.error('Error updating session context:', error);
      throw new Error(error.response?.data?.error || 'Failed to update session context');
    }
  }

  // Get session statistics
  async getSessionStats(sessionId) {
    try {
      const [messages, actions] = await Promise.all([
        this.getSessionMessages(sessionId),
        this.getSessionActions(sessionId)
      ]);

      return {
        messageCount: messages.length,
        actionCount: actions.length,
        lastActivity: messages.length > 0 ? messages[messages.length - 1].timestamp : null
      };
    } catch (error) {
      console.error('Error getting session stats:', error);
      throw new Error(error.response?.data?.error || 'Failed to get session stats');
    }
  }
}

export const chatbotApi = new ChatbotApi();
